import type { Edge, Node } from 'reactflow';

/** Canvas node ids to expand in the outline (parents of `focusCanvasNodeId`, excluding `start-node`). */
export function collectExpandIdsForCanvasNode(focusCanvasNodeId: string, edges: Edge[]): string[] {
    const parentByTarget = new Map<string, string>();
    for (const e of edges) {
        parentByTarget.set(e.target, e.source);
    }
    const ids: string[] = [];
    let cur = focusCanvasNodeId;
    while (parentByTarget.has(cur)) {
        const p = parentByTarget.get(cur)!;
        if (p === 'start-node') break;
        ids.push(p);
        cur = p;
    }
    return ids;
}

export type OutlineNodeKind = 'user_flow' | 'hlr' | 'llr' | 'test_case';

export interface OutlineTreeNode {
    id: string;
    kind: OutlineNodeKind;
    label: string;
    /** React Flow node id used for fitView / highlight */
    focusNodeId: string;
    /** Only for test_case — pick row inside LLR detail modal */
    testCaseUiid?: string;
    children: OutlineTreeNode[];
}

function nodeOrderKey(n: Node): number {
    const sno = (n.data as { sno?: number })?.sno;
    return typeof sno === 'number' ? sno : 0;
}

function childNodesFor(parentId: string, edges: Edge[], nodes: Node[]): Node[] {
    const childIds = new Set(edges.filter((e) => e.source === parentId).map((e) => e.target));
    return nodes.filter((n) => childIds.has(n.id)).sort((a, b) => nodeOrderKey(a) - nodeOrderKey(b));
}

/** Same graph as the canvas: user flows → HLR → LLR; test cases from each LLR’s originalData.test_case_list. */
export function buildOutlineTreeFromCanvas(nodes: Node[], edges: Edge[]): OutlineTreeNode[] {
    const userFlows = nodes
        .filter((n) => n.type === 'userFlowNode' && n.id.startsWith('userflow_'))
        .sort((a, b) => nodeOrderKey(a) - nodeOrderKey(b));

    const buildTestCases = (llr: Node): OutlineTreeNode[] => {
        const list = ((llr.data as { originalData?: { test_case_list?: Array<{ uiid?: string; id?: number; name?: string }> } })
            ?.originalData?.test_case_list || []) as Array<{ uiid?: string; id?: number; name?: string }>;
        return list.map((tc, index) => {
            const uiid = String(tc.uiid ?? tc.id ?? `idx-${index}`);
            return {
                id: `outline-tc:${llr.id}:${uiid}`,
                kind: 'test_case' as const,
                label: tc.name?.trim() || `Test case ${index + 1}`,
                focusNodeId: llr.id,
                testCaseUiid: uiid,
                children: [],
            };
        });
    };

    const buildLlr = (llr: Node): OutlineTreeNode => ({
        id: llr.id,
        kind: 'llr',
        label: ((llr.data as { name?: string })?.name || '').trim() || 'Low-level requirement',
        focusNodeId: llr.id,
        children: buildTestCases(llr),
    });

    const buildHlr = (hlr: Node): OutlineTreeNode => {
        const llrs = childNodesFor(hlr.id, edges, nodes).filter((n) => n.type === 'lowLevelRequirementNode');
        return {
            id: hlr.id,
            kind: 'hlr',
            label: ((hlr.data as { name?: string })?.name || '').trim() || 'High-level requirement',
            focusNodeId: hlr.id,
            children: llrs.map(buildLlr),
        };
    };

    return userFlows.map((uf) => {
        const hlrs = childNodesFor(uf.id, edges, nodes).filter((n) => n.type === 'highLevelRequirementNode');
        return {
            id: uf.id,
            kind: 'user_flow',
            label: ((uf.data as { name?: string })?.name || '').trim() || 'User flow',
            focusNodeId: uf.id,
            children: hlrs.map(buildHlr),
        };
    });
}

export function collectOutlineBranchIds(tree: OutlineTreeNode[]): Set<string> {
    const ids = new Set<string>();
    const walk = (n: OutlineTreeNode) => {
        ids.add(n.id);
        n.children.forEach(walk);
    };
    tree.forEach(walk);
    return ids;
}

/** Ids of nodes that have outline children (expand applies to these only). */
export function collectExpandableOutlineIds(tree: OutlineTreeNode[]): Set<string> {
    const ids = new Set<string>();
    const walk = (n: OutlineTreeNode) => {
        if (n.children.length > 0) ids.add(n.id);
        n.children.forEach(walk);
    };
    tree.forEach(walk);
    return ids;
}
